import { Injectable } from '@angular/core';
import { ConfigService } from '../../config/config.service';
import { timer, Observable, of } from 'rxjs';
import { Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { jwtDecode, JwtPayload } from "jwt-decode";
import { IdentityVerificationResultStateHelper } from '../../../core/data/identity-verification/features/identity-verification-result-content/identity-verification-result-state.helper';
import { SessionStorageKey, SessionStorageState } from '../../helpers/session-storage.helper';
import { getSafeOAuthSessionTimeoutMs } from './oauth-session-timeout.helper';

declare const gapi: any;

@Injectable({
  providedIn: 'root'
})
export class OAuthService {
  constructor(
    protected configService: ConfigService,
    protected router: Router,
    protected http: HttpClient) {
  }

  static oauthSessionTimeout: any;

  get accessToken(): string {
    return sessionStorage.token;
  }

  transformRequest(obj): string {
    const str = [];
    for (const p in obj)
      str.push(encodeURIComponent(p) + "=" + encodeURIComponent(obj[p]));
    return str.join("&");
  }

  refreshAuthToken(): Observable<any> {
    if (!sessionStorage.refreshToken) {
      this.sessionEndLocal();
      return of(null);
    }
    const body = this.transformRequest({
      grant_type: "refresh_token",
      client_id: sessionStorage.loginType,
      refresh_token: sessionStorage.refreshToken
    });
    return this.http.post(`${this.configService.environment.oauth.url}/token`, body, {
      headers: { "Content-Type": "application/x-www-form-urlencoded" }
    });
  }

  get authenticated(): boolean {
    const tokenExpiresAt = sessionStorage.tokenExpiresAt
      ? new Date(sessionStorage.tokenExpiresAt)
      : undefined;
    const tokenExpired = tokenExpiresAt
      && tokenExpiresAt.getTime() < new Date().getTime();

    return this.accessToken && tokenExpiresAt && !tokenExpired;
  }

  /** SSO session id (the access token `sso` claim); shared across apps of one login. */
  getSessionId(): string {
    if (!this.accessToken) {
      return null;
    }
    return jwtDecode<{ sso: string }>(this.accessToken).sso;
  }

  sessionBegin(loginType: string, accessToken: string, expiresIn: number, refreshToken: string, refresh_token_expires_in?: number, clientId?: string): void {
    const now = new Date();

    sessionStorage.loginType = loginType;
    sessionStorage.token = accessToken;
    sessionStorage.tokenSetAt = now;
    sessionStorage.tokenExpiresAt = new Date(now.getTime() + expiresIn * 1000);
    sessionStorage.refreshToken = refreshToken;

    if (clientId) {
      sessionStorage.clientId = clientId;
    }

    if (refresh_token_expires_in) {
      sessionStorage.refreshTokenExpiresAt = new Date(now.getTime() + refresh_token_expires_in * 1000);
    }

    this.restartIdleTimer();
  }

  sessionEndLocal(): void {
    this.sessionTimeoutClear();
    sessionStorage.removeItem('loginType');
    sessionStorage.removeItem('token');
    sessionStorage.removeItem('tokenSetAt');
    sessionStorage.removeItem('tokenExpiresAt');
    sessionStorage.removeItem('refreshToken');
    sessionStorage.removeItem('refreshTokenExpiresAt');
    sessionStorage.removeItem('relinkAccountViewed');
    sessionStorage.removeItem('referralPageViewed');
    sessionStorage.removeItem('insurancePageViewed');
    sessionStorage.removeItem('bankActionRequiredViewed');
    sessionStorage.removeItem('renewalEligibilityPageViewed');
    sessionStorage.removeItem('campaign');
    sessionStorage.removeItem('navigationHistory');
    IdentityVerificationResultStateHelper.clear();
    SessionStorageState.remove(SessionStorageKey.AccountSetupViewed);
    SessionStorageState.remove(SessionStorageKey.ApplicationAgreementViewed);
    SessionStorageState.remove(SessionStorageKey.AccountAgreementViewed);
    SessionStorageState.remove(SessionStorageKey.IdVerseReturnPending);
    SessionStorageState.remove(SessionStorageKey.LoyaltyRewardPopupShownForLoanIds);
    SessionStorageState.remove(SessionStorageKey.IdVerseCompletedCallback);
  }

  protected sessionTimeoutClear(): void {
    if (OAuthService.oauthSessionTimeout && OAuthService.oauthSessionTimeout.unsubscribe) {
      OAuthService.oauthSessionTimeout.unsubscribe();
    }

    OAuthService.oauthSessionTimeout = null;
  }

  restartIdleTimer(): void {
    this.sessionTimeoutClear();
    sessionStorage.removeItem("sessionExpiredAutoLogout");

    if (!sessionStorage.tokenExpiresAt || !sessionStorage.refreshToken) return;

    const refreshToken = jwtDecode<JwtPayload>(sessionStorage.refreshToken);

    const signOutDueTimeInMs = getSafeOAuthSessionTimeoutMs({
      accessTokenExpiresAtMs: new Date(sessionStorage.tokenExpiresAt).getTime(),
      refreshTokenExpiresAtMs: refreshToken.exp * 1000,
    });

    OAuthService.oauthSessionTimeout = timer(signOutDueTimeInMs).subscribe(() => {
      this.sessionEndLocal();
      sessionStorage.setItem("sessionExpiredAutoLogout", "true");
      this.router.navigate(["sign-out"], { replaceUrl: true });
    });

  }

  saveNavigationHistory(): void {
    if (window.location.pathname !== "/mfa") {
      const params = new URLSearchParams(window.location.search);
      params.delete("sessionId");
      sessionStorage.setItem("navigation.history", window.location.pathname);
      sessionStorage.setItem(
        "navigation.historyQuery",
        `${window.location.pathname}?${params.toString()}`
      );
    }
  }
}
